import React, { useEffect, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDictionaryHandling } from 'hooks/dictionaryHandling';
import { useInsuranceTypeConfiguration, useAuthorizations, useAuthUserData } from 'hooks';
import { useHeader, useHeaderLinks } from 'providers/AreasProvider';
import { Tooltip, Grid, CircularProgress, useTheme, useMediaQuery, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { emptyString, insuranceTypeNames } from 'constants/defaultValues';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import TimelineIcon from '@mui/icons-material/Timeline';
import NotesIcon from '@mui/icons-material/Notes';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CloseIcon from '@mui/icons-material/Close';
import { get } from '@totalsoft/change-tracking-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { InsuranceHeaderActionsSpeedDial, InsuranceHeaderActionsWrapper, InsuranceHeaderTitle } from './StepperStyles';

const InsuranceDocumentHeader = ({
  isNew,
  documentId,
  onConfirmDuplication,
  duplicationInProgress,
  documentHeaderData,
  onChangeDocumentState,
  documentAllowedTransitions,
  documentAllowedTransitionsLoading,
  actionsVisibility
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [
      AuthorizationCodes.hasRightToViewComments,
      AuthorizationCodes.hasRightToViewStateTransitionsHistory,
      AuthorizationCodes.hasRightToViewChecks,
      AuthorizationCodes.hasRightToEditWorkflow,
      AuthorizationCodes.hasRightToViewInsuranceServiceLogs
    ],
    documentTypeId,
    documentStateId
  );
  const { authUser } = useAuthUserData();
  const { t } = useTranslation();
  const [, setHeader] = useHeader();
  const [, setHeaderLinks] = useHeaderLinks();
  const history = useHistory();
  const { rcaInsuranceTypeConfiguration, cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.RCA, insuranceTypeNames.CASCO)
  );
  const theme = useTheme();
  const underMd = useMediaQuery(theme.breakpoints.down('md'));

  const { getCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(
    () => getCodeById(documentHeaderData?.insuranceTypeId),
    [documentHeaderData.insuranceTypeId, getCodeById]
  );

  const handleChangeDocumentState = useCallback(value => onChangeDocumentState(value), [onChangeDocumentState]);

  useEffect(() => {
    setHeader(
      isNew ? (
        <InsuranceHeaderTitle variant='subtitle1'>{t('Offer.List.Field.NewOffer')}</InsuranceHeaderTitle>
      ) : (
        <Grid variant='subtitle1' container alignItems='center'>
          <Grid item>
            <InsuranceHeaderTitle id='documentNumberHeader' variant='subtitle1'>
              {t('Offer.List.Field.DocumentNumberHeader', {
                documentNumber: documentHeaderData?.documentNumber || emptyString,
                documentDate: t('DATE', {
                  date: documentHeaderData?.documentDate || emptyString
                })
              })}
            </InsuranceHeaderTitle>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Tooltip title={documentHeaderData?.documentStateName || ''} placement='right'>
              <div>
                <Autocomplete
                  id='offerHeader_documentState'
                  fullWidth
                  simpleValue={true}
                  onChange={handleChangeDocumentState}
                  value={documentHeaderData.documentStateId}
                  options={documentAllowedTransitions || []}
                  disabled={documentAllowedTransitionsLoading}
                />
              </div>
            </Tooltip>
          </Grid>
        </Grid>
      )
    );
  }, [setHeader, documentAllowedTransitions, documentHeaderData, t, isNew, documentAllowedTransitionsLoading, handleChangeDocumentState]);

  const handleBackToListClick = useCallback(() => {
    history.push('/insurance-documents');
  }, [history]);

  const handlePrintDocumentsClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/documents`);
  }, [documentId, history]);

  const handleWorkflowClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/workflows`);
  }, [documentId, history]);

  const handleNotesClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/notes`);
  }, [documentId, history]);

  const handleVerificationClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/verifications`);
  }, [documentId, history]);

  const handleStateTransitionClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/state-transitions`);
  }, [documentId, history]);

  const handleServiceLogsClick = useCallback(() => {
    history.push(`/insurance-documents/${documentId}/insurance-service-logs`);
  }, [documentId, history]);

  const headerActions = useMemo(
    () => [
      {
        name: 'BackToList',
        icon: <ArrowBackIcon />,
        handler: handleBackToListClick,
        tooltip: t('Tooltips.BackToList'),
        visible: true
      },
      {
        name: 'DuplicateOffer',
        icon: duplicationInProgress |> get ? <CircularProgress color='secondary' size={27} /> : <FileCopyIcon />,
        handler: onConfirmDuplication,
        tooltip: t('Tooltips.DuplicateOffer'),
        visible: actionsVisibility?.duplication && authUser?.authorizations?.hasRightToAddOffer,
        disabled: duplicationInProgress |> get
      },
      {
        name: 'Documents',
        icon: <PrintIcon />,
        handler: handlePrintDocumentsClick,
        tooltip: t('Tooltips.Documents'),
        visible: true
      },
      {
        name: 'Notes',
        icon: <NotesIcon />,
        handler: handleNotesClick,
        tooltip: t('Tooltips.Notes'),
        visible: authorizations?.hasRightToViewComments
      },
      {
        name: 'StateTransitions',
        icon: <CompareArrowsIcon />,
        handler: handleStateTransitionClick,
        tooltip: t('Tooltips.StateTransitions'),
        visible: authorizations?.hasRightToViewStateTransitionsHistory
      },
      {
        name: 'Workflow',
        icon: <TimelineIcon />,
        handler: handleWorkflowClick,
        tooltip: t('Tooltips.Workflow'),
        visible:
          (insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode ||
            insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode) &&
          authorizations?.hasRightToEditWorkflow
      },
      {
        name: 'Verifications',
        icon: <DoneAllIcon />,
        handler: handleVerificationClick,
        tooltip: t('Tooltips.Verifications'),
        visible: authorizations?.hasRightToViewChecks
      },
      {
        name: 'ServiceLogs',
        icon: <SettingsEthernetIcon />,
        handler: handleServiceLogsClick,
        tooltip: t('Tooltips.ServiceLogs'),
        visible:
          insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode &&
          authorizations?.hasRightToViewInsuranceServiceLogs &&
          actionsVisibility?.insuranceServiceLogs
      }
    ],
    [
      actionsVisibility,
      authUser,
      authorizations,
      cascoInsuranceTypeConfiguration,
      duplicationInProgress,
      handleBackToListClick,
      handleNotesClick,
      handlePrintDocumentsClick,
      handleServiceLogsClick,
      handleStateTransitionClick,
      handleVerificationClick,
      handleWorkflowClick,
      insuranceTypeCode,
      onConfirmDuplication,
      rcaInsuranceTypeConfiguration,
      t
    ]
  );

  useEffect(() => {
    !isNew &&
      setHeaderLinks(
        <InsuranceHeaderActionsWrapper>
          {underMd && (
            <InsuranceHeaderActionsSpeedDial
              ariaLabel='insuranceHeaderActions'
              direction='left'
              FabProps={{ size: 'medium' }}
              icon={<SpeedDialIcon icon={<MoreHorizIcon />} openIcon={<CloseIcon />} />}
            >
              {headerActions
                .slice()
                .reverse()
                .filter(action => action.visible)
                .map(({ name, icon, handler, tooltip, disabled }) => (
                  <SpeedDialAction key={name} icon={icon} onClick={handler} tooltipTitle={tooltip} FabProps={{ disabled }} />
                ))}
            </InsuranceHeaderActionsSpeedDial>
          )}
          {!underMd &&
            headerActions
              .filter(action => action.visible)
              .map(({ name, icon, handler, tooltip, disabled }) => (
                <IconButton key={name} onClick={handler} tooltip={tooltip} variant='text' color='primary' disabled={disabled}>
                  {icon}
                </IconButton>
              ))}
        </InsuranceHeaderActionsWrapper>
      );
  }, [headerActions, isNew, setHeaderLinks, underMd]);

  return <></>;
};

InsuranceDocumentHeader.propTypes = {
  documentId: PropTypes.number,
  isNew: PropTypes.bool,
  onConfirmDuplication: PropTypes.func.isRequired,
  duplicationInProgress: PropTypes.object.isRequired,
  documentHeaderData: PropTypes.object.isRequired,
  onChangeDocumentState: PropTypes.func.isRequired,
  documentAllowedTransitions: PropTypes.array.isRequired,
  documentAllowedTransitionsLoading: PropTypes.bool.isRequired,
  actionsVisibility: PropTypes.object
};

export default InsuranceDocumentHeader;
